/**
 * negociationTracking.js — Util de tracking du funnel Négociation → Spécialiste.
 *
 * Objectif : mesurer dans Google Analytics 4 / Looker le taux de conversion
 * « Dossier rouge → Clic CTA spécialiste → Création de mission » en gardant un
 * identifiant unique par dossier (utm_case) et le score de maturité (utm_score).
 *
 * Charte : aucune donnée nominative dans les UTM (juste case_id + score + profile).
 */

const UTM_SOURCE = "confilex_negociation";

/**
 * Construit l'URL d'un CTA spécialiste avec UTM + paramètres CONFILEX.
 * @param {string} route — Route cible (ex: "/trouver-un-avocat")
 * @param {object} opts — { caseId, profile, level, pertinence, action }
 * @returns {string} URL absolue prête à utiliser dans <Link to=...>
 */
export function buildExpertCtaUrl(route, { caseId, profile, level, pertinence, action = "cta_click" }) {
  if (!route) return "/";
  const params = new URLSearchParams();
  params.set("from_case", caseId || "");
  params.set("utm_source", UTM_SOURCE);
  params.set("utm_medium", action);
  params.set("utm_campaign", `negociation_${level || "unknown"}`);
  if (profile) params.set("utm_content", profile);
  if (caseId) params.set("utm_case", caseId.slice(0, 8));
  if (pertinence != null) params.set("utm_score", String(pertinence));
  return `${route}?${params.toString()}`;
}

/**
 * Push un évènement GA4 (gtag) si disponible. No-op si gtag absent.
 */
export function trackExpertLead({ caseId, profile, level, pertinence, action }) {
  try {
    if (typeof window === "undefined") return;
    const payload = {
      event_category: "negociation",
      event_label: profile || "unknown",
      level: level || "unknown",
      pertinence: pertinence ?? null,
      case_id_short: caseId ? caseId.slice(0, 8) : null,
      value: pertinence ?? 0,
    };
    // gtag
    if (typeof window.gtag === "function") {
      window.gtag("event", action || "expert_lead_click", payload);
    }
    // dataLayer fallback
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: action || "expert_lead_click", ...payload });
    }
  } catch {
    /* swallow — analytics ne doit jamais casser l'UX */
  }
}

/**
 * Notifie le backend du clic (pour Looker Studio) — fire-and-forget.
 */
export async function pingBackendLead({ caseId, profile, action, pertinence, level }) {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/negotiation/cases/${caseId}/lead-click`;
    const token = localStorage.getItem("access_token");
    await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ profile, action, pertinence, level }),
      keepalive: true,
    });
  } catch {
    /* ignore */
  }
}

/**
 * Wrapper haut-niveau : track + ping + retourne l'URL à naviguer.
 */
export function emitExpertLead({ route, caseId, profile, level, pertinence, action = "cta_click" }) {
  trackExpertLead({ caseId, profile, level, pertinence, action });
  pingBackendLead({ caseId, profile, level, pertinence, action });
  return buildExpertCtaUrl(route, { caseId, profile, level, pertinence, action });
}
